import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Plus, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useI18n } from "@/context/I18nContext";

const DEFAULT_ORDER = [
    "sandwiches",
    "club-sandwiches",
    "grinders",
    "wraps",
    "salads",
    "fries",
    "mofongos",
    "rice-and-beans",
    "made-to-order",
    "dominican",
    "empanadas",
    "juices",
    "drinks",
];
const formatCat = (c) =>
    c
        .split("-")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");

export default function Menu() {
    const { lang, t } = useI18n();
    const { add, setOpen } = useCart();
    const [items, setItems] = useState([]);
    const [active, setActive] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/menu")
            .then((r) => setItems(r.data.items || []))
            .catch(() => toast.error("Couldn't load menu"))
            .finally(() => setLoading(false));
    }, []);

    // Discover categories from the loaded items, preserving the default
    // visual order and appending any custom ones the owner has added.
    const categories = useMemo(() => {
        const found = new Set(items.map((i) => i.category).filter(Boolean));
        const ordered = DEFAULT_ORDER.filter((c) => found.has(c));
        const extras = Array.from(found)
            .filter((c) => !DEFAULT_ORDER.includes(c))
            .sort();
        return [...ordered, ...extras];
    }, [items]);

    // Pick the first available category once items have loaded.
    useEffect(() => {
        if (!active && categories.length > 0) setActive(categories[0]);
    }, [categories, active]);

    const filtered = useMemo(
        () => items.filter((i) => i.category === active),
        [items, active],
    );

    const labelFor = (c) => {
        // i18n has labels for the original 4 categories; everything else
        // falls back to a Title-Cased version of the slug.
        const key = `menu.categories.${c}`;
        const translated = t(key);
        return translated === key ? formatCat(c) : translated;
    };

    const handleAdd = (item) => {
        add({ id: item.id, name: lang === "en" ? item.name_en : item.name_es, price: item.price, image: item.image });
        toast.success(`${lang === "en" ? item.name_en : item.name_es} +1`, {
            action: { label: t("cta.view_cart"), onClick: () => setOpen(true) },
        });
    };

    // Horizontal scroll controls for the category strip.
    const scrollerRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollState = () => {
        const el = scrollerRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 8);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
    };

    useEffect(() => {
        updateScrollState();
        const el = scrollerRef.current;
        if (!el) return;
        el.addEventListener("scroll", updateScrollState, { passive: true });
        window.addEventListener("resize", updateScrollState);
        return () => {
            el.removeEventListener("scroll", updateScrollState);
            window.removeEventListener("resize", updateScrollState);
        };
    }, [categories.length]);

    // After clicking a tab, keep it in view so the user can see what's selected.
    useEffect(() => {
        const el = scrollerRef.current;
        if (!el || !active) return;
        const btn = el.querySelector(`[data-testid="category-${active}"]`);
        if (btn) {
            const left = btn.offsetLeft - el.clientWidth / 2 + btn.clientWidth / 2;
            el.scrollTo({ left, behavior: "smooth" });
        }
    }, [active]);

    const scrollBy = (dir) => {
        const el = scrollerRef.current;
        if (!el) return;
        el.scrollBy({ left: dir * Math.max(280, el.clientWidth * 0.7), behavior: "smooth" });
    };

    return (
        <section id="menu" data-testid="menu-section" className="bg-background py-20 md:py-28">
            <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary">
                            {t("menu.title")}
                        </p>
                        <h2 className="font-display font-black tracking-tighter text-4xl md:text-6xl mt-2">
                            {t("menu.subtitle")}
                        </h2>
                    </div>
                </div>

                {/* Category tabs with horizontal scroll + arrow controls */}
                <div className="relative sticky top-16 z-20 -mx-4 px-4 mb-8 bg-background/90 backdrop-blur-md">
                    {/* Left arrow */}
                    <button
                        type="button"
                        aria-label="Scroll categories left"
                        data-testid="categories-scroll-left"
                        onClick={() => scrollBy(-1)}
                        className={`hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-30 h-10 w-10 items-center justify-center rounded-full bg-white shadow-lg border border-border transition-all ${
                            canScrollLeft ? "opacity-100 hover:scale-105" : "opacity-0 pointer-events-none"
                        }`}
                    >
                        <CaretLeft size={18} weight="bold" className="text-foreground" />
                    </button>
                    {/* Right arrow */}
                    <button
                        type="button"
                        aria-label="Scroll categories right"
                        data-testid="categories-scroll-right"
                        onClick={() => scrollBy(1)}
                        className={`hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-30 h-10 w-10 items-center justify-center rounded-full bg-white shadow-lg border border-border transition-all ${
                            canScrollRight ? "opacity-100 hover:scale-105" : "opacity-0 pointer-events-none"
                        }`}
                    >
                        <CaretRight size={18} weight="bold" className="text-foreground" />
                    </button>
                    {/* Fade edges to hint at more content */}
                    <div
                        aria-hidden
                        className={`pointer-events-none absolute left-4 top-0 bottom-2 w-10 z-10 bg-gradient-to-r from-background to-transparent transition-opacity ${
                            canScrollLeft ? "opacity-100" : "opacity-0"
                        }`}
                    />
                    <div
                        aria-hidden
                        className={`pointer-events-none absolute right-4 top-0 bottom-2 w-10 z-10 bg-gradient-to-l from-background to-transparent transition-opacity ${
                            canScrollRight ? "opacity-100" : "opacity-0"
                        }`}
                    />
                    <div
                        ref={scrollerRef}
                        data-testid="menu-categories"
                        className="flex gap-2 md:gap-3 overflow-x-auto scrollbar-hide pb-2 scroll-smooth"
                    >
                        {categories.map((c) => (
                            <button
                                key={c}
                                data-testid={`category-${c}`}
                                onClick={() => setActive(c)}
                                className={`shrink-0 px-5 py-2.5 rounded-full font-bold text-sm uppercase tracking-wider transition-all border-2 ${
                                    active === c
                                        ? "bg-foreground border-foreground text-background"
                                        : "bg-white border-border text-foreground/70 hover:border-foreground"
                                }`}
                            >
                                {labelFor(c)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Grid */}
                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="h-72 rounded-2xl bg-muted animate-pulse" />
                        ))}
                    </div>
                ) : (
                    <motion.div
                        layout
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
                    >
                        {filtered.map((item, idx) => (
                            <motion.article
                                key={item.id}
                                layout
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: idx * 0.04 }}
                                data-testid={`menu-item-${item.id}`}
                                className="group bg-card rounded-2xl border border-border/70 overflow-hidden flex flex-col hover:shadow-xl hover:-translate-y-1 transition-all"
                            >
                                <div className="relative h-52 overflow-hidden bg-muted">
                                    <img
                                        src={item.image}
                                        alt={item.name_en}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                        loading="lazy"
                                    />
                                    {item.tag && (
                                        <span className="absolute top-3 left-3 bg-secondary text-secondary-foreground text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                                            {item.tag}
                                        </span>
                                    )}
                                </div>
                                <div className="p-5 flex-grow flex flex-col">
                                    <div className="flex items-start justify-between gap-3">
                                        <h3 className="font-display font-bold text-xl leading-tight">
                                            {lang === "en" ? item.name_en : item.name_es}
                                        </h3>
                                        <span className="font-display font-black text-lg shrink-0">
                                            ${item.price.toFixed(2)}
                                        </span>
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed flex-grow">
                                        {lang === "en" ? item.desc_en : item.desc_es}
                                    </p>
                                    <button
                                        data-testid={`add-${item.id}`}
                                        onClick={() => handleAdd(item)}
                                        className="mt-4 self-stretch inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full py-3 font-bold uppercase tracking-wide text-sm transition-all hover:-translate-y-0.5"
                                    >
                                        <Plus size={16} weight="bold" />
                                        {t("cta.add")}
                                    </button>
                                </div>
                            </motion.article>
                        ))}
                    </motion.div>
                )}
            </div>
        </section>
    );
}
